<template>
  <div class="pre-login-root">
    <thankyou-layout />
    <div class="contentWrapper" :style="preLoginLayout">
      <ads
        v-if="shouldRenderAds"
        :key="`${adIntegrationId}-prelogin`"
        class="ads-area"
        :campaign-id="adIntegrationId"
        :zone-id="adIntegrationId"
        :position="adPosition"
        type="pre"
        :skip-enabled="skipEnabled"
        :skip-delay="skipDelay"
        @ad-loaded="onAdLoaded"
        @ad-started="onAdStarted"
        @ad-completed="onAdCompleted"
        @ad-error="onAdError"
        @ad-error-timer-start="onAdErrorTimerStart"
        @ad-skipped="onAdSkipped"
      />
      <div :class="['postLogin-container', { 'preview-mode': hasPreviewAds && hasAd }]">
        <ads-placeholder v-if="hasPreviewAds && hasAd" />
      </div>
    </div>
  </div>
</template>

<script>
  import Ads from '../components/ads/ads'
  import AdsPlaceholder from './ads-placeholder'
  import ThankyouLayout from './thankyouLayout.vue'
  import { logUserAction } from '@/utils/logging'
  import debugLogger from '@/utils/DebugLoggerSDK'
  import screens from '../utils/pageSchema'

  const logPreLoginEvent = async (message, data = {}, level = 'info') => {
    const logMethod =
      typeof debugLogger[level] === 'function' ? debugLogger[level] : debugLogger.info
    logMethod.call(debugLogger, message, data)
  }

  export default {
    name: 'PreLoginAd',

    components: {
      ThankyouLayout,
      Ads,
      AdsPlaceholder,
    },

    data() {
      return {
        error: null,
        timer: null,
        intervalId: null,
        timerStarted: false,
        adLoaded: false,
        adStarted: false,
        showNextButton: false,
        errorTimerDuration: null,
        isSambaAd: false,
      }
    },

    computed: {
      baseData() {
        return this.$store.state.baseData
      },

      adConfig() {
        return this.baseData?.advertisement?.preLogin || {}
      },
      
      hasAd() {
        return !!this.adConfig.integrationId
      },

      adIntegrationId() {
        return this.adConfig.integrationId
      },

      adPosition() {
        return 'fullpage'
      },

      skipEnabled() {
        return this.adConfig.skipEnabled === true
      },

      skipDelay() {
        return this.adConfig.skipDelay !== undefined ? this.adConfig.skipDelay : 5
      },

      nextRoute() {
        const flags = this.baseData?.flags || {}
        let route = '/social-logins'

        if (flags.isConsent) {
          route = '/consent'
        } else if (flags.isCovid) {
          route = '/covid'
        } else if (flags.isPaidCampaign) {
          route = '/payment-plans'
        }

        return route
      },

      hasPreviewAds() {
        const isPreview = this.baseData?.development?.preview || false
        const hasIntegration = !!this.adConfig.integrationId
        return isPreview && hasIntegration
      },

      preLoginLayout() {
        return {
          background: this.baseData?.colors?.background || '#FFFFFF',
          fontFamily: this.baseData?.fonts?.font || "'Avenir Next', sans-serif",
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
        }
      },

      shouldRenderAds() {
        return this.hasAd && !this.hasPreviewAds && this.adIntegrationId
      },
    },

    mounted() {
      try {
        logPreLoginEvent('[PRE-LOGIN] Mounted - START', {
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          url: window.location.href,
          referrer: document.referrer,
          viewport: {
            width: window.innerWidth,
            height: window.innerHeight,
          },
          storeState: {
            hasBaseData: !!this.$store.state.baseData,
            baseDataKeys: this.$store.state.baseData ? Object.keys(this.$store.state.baseData) : [],
          },
        })

        this.initializePage()

        logPreLoginEvent('[PRE-LOGIN] Mounted - END', {
          error: this.error,
          hasAd: this.hasAd,
          hasPreviewAds: this.hasPreviewAds,
          adIntegrationId: this.adIntegrationId,
        })
      } catch (e) {
        this.error = e
        logPreLoginEvent('[PRE-LOGIN] Mounted - ERROR', {
          error: e?.message || String(e),
          stack: e?.stack,
          errorString: String(e),
        })
      }
    },

    beforeDestroy() {
      logPreLoginEvent('[PRE-LOGIN] beforeDestroy - START', {
        hasIntervalId: !!this.intervalId,
        timer: this.timer,
        timerStarted: this.timerStarted,
        adLoaded: this.adLoaded,
        adStarted: this.adStarted,
        showNextButton: this.showNextButton,
      })

      if (this.intervalId) {
        clearInterval(this.intervalId)
        logPreLoginEvent('[PRE-LOGIN] beforeDestroy - cleared interval', {})
      }
      this.restoreScrollbars()
    },

    methods: {
      hideScrollbars() {
        logPreLoginEvent('[PRE-LOGIN] hideScrollbars', {
          hasDocument: !!document,
          hasBody: !!document.body,
          hasDocumentElement: !!document.documentElement,
        })

        document.body.style.overflow = 'hidden'
        document.body.style.width = '100vw'
        document.body.style.maxWidth = '100vw'
        document.body.style.height = '100vh'
        document.body.style.maxHeight = '100vh'

        document.documentElement.style.overflow = 'hidden'
        document.documentElement.style.width = '100vw'
        document.documentElement.style.maxWidth = '100vw'
        document.documentElement.style.height = '100vh'
        document.documentElement.style.maxHeight = '100vh'

        logPreLoginEvent('[PRE-LOGIN] hideScrollbars - completed', {})
      },

      restoreScrollbars() {
        logPreLoginEvent('[PRE-LOGIN] restoreScrollbars', {
          hasDocument: !!document,
          hasBody: !!document.body,
          hasDocumentElement: !!document.documentElement,
        })

        document.body.style.overflow = ''
        document.body.style.width = ''
        document.body.style.maxWidth = ''
        document.body.style.height = ''
        document.body.style.maxHeight = ''

        document.documentElement.style.overflow = ''
        document.documentElement.style.width = ''
        document.documentElement.style.maxWidth = ''
        document.documentElement.style.height = ''
        document.documentElement.style.maxHeight = ''

        logPreLoginEvent('[PRE-LOGIN] restoreScrollbars - completed', {})
      },

      async initializePage() {
        await logPreLoginEvent('[PRE-LOGIN] initializePage - START', {
          hasAd: this.hasAd,
          hasPreviewAds: this.hasPreviewAds,
          adIntegrationId: this.adIntegrationId,
          skipEnabled: this.skipEnabled,
          skipDelay: this.skipDelay,
          baseData: {
            exists: !!this.baseData,
            hasAdvertisement: !!this.baseData?.advertisement,
            hasPreLogin: !!this.baseData?.advertisement?.preLogin,
            isPreview: this.baseData?.development?.preview || false,
          },
          adConfig: this.adConfig,
          nextRoute: this.nextRoute,
        })

        logUserAction({
          type: 'page_view',
          label: 'Pre-Login Ad Page',
          data: {
            hasAd: this.hasAd,
            skipEnabled: this.skipEnabled,
            skipDelay: this.skipDelay,
          },
        })

        await logPreLoginEvent('[PRE-LOGIN] initializePage - logged user action', {
          type: 'page_view',
          label: 'Pre-Login Ad Page',
          data: {
            hasAd: this.hasAd,
            skipEnabled: this.skipEnabled,
            skipDelay: this.skipDelay,
          },
        })

        if (!this.hasAd) {
          await logPreLoginEvent('[PRE-LOGIN] initializePage - no ads, navigating', {
            hasAd: this.hasAd,
            adConfig: this.adConfig,
            nextRoute: this.nextRoute,
          })
          this.navigateToNext()
        } else {
          await logPreLoginEvent('[PRE-LOGIN] initializePage - has ads, waiting for ad component', {
            hasAd: this.hasAd,
            hasPreviewAds: this.hasPreviewAds,
            adIntegrationId: this.adIntegrationId,
            willShowAds: this.hasAd && !this.hasPreviewAds && this.adIntegrationId,
          })
        }

        await logPreLoginEvent('[PRE-LOGIN] initializePage - END', {})
      },

      async onAdLoaded(adData) {
        await logPreLoginEvent('[PRE-LOGIN] onAdLoaded - START', {
          adData,
          adDataType: typeof adData,
          isSambaAd: adData?.isSambaAd,
          isBrokenAd: adData?.isBrokenAd,
          previousAdLoaded: this.adLoaded,
          adIntegrationId: this.adIntegrationId,
          adPosition: this.adPosition,
          timestamp: new Date().toISOString(),
        })

        this.adLoaded = true

        await logPreLoginEvent('[PRE-LOGIN] onAdLoaded - set adLoaded=true', {
          adLoaded: this.adLoaded,
        })

        logUserAction({
          type: 'ad_event',
          label: 'Pre-Login Ad Loaded',
          data: {
            integrationId: this.adIntegrationId,
            position: this.adPosition,
          },
        })

        await logPreLoginEvent('[PRE-LOGIN] onAdLoaded - sent event', {
          type: 'ad_event',
          label: 'Pre-Login Ad Loaded',
          data: {
            integrationId: this.adIntegrationId,
            position: this.adPosition,
          },
        })

        if (adData?.isBrokenAd) {
          await logPreLoginEvent(
            '[PRE-LOGIN] onAdLoaded - broken ad detected, scheduling navigation',
            {
              isBrokenAd: adData.isBrokenAd,
              nextRoute: this.nextRoute,
            },
          )

          await this.navigateToNext()
          return
        }

        const isSambaAd = adData?.isSambaAd || false

        if (!isSambaAd) {
          await logPreLoginEvent('[PRE-LOGIN] onAdLoaded - non-Samba ad loaded (static content)', {
            isSambaAd,
            skipEnabled: this.skipEnabled,
            timerStarted: this.timerStarted,
          })

          if (this.skipEnabled && !this.timerStarted) {
            await logPreLoginEvent('[PRE-LOGIN] onAdLoaded - starting skip timer')
            this.startTimer()
          } else if (!this.skipEnabled) {
            await logPreLoginEvent('[PRE-LOGIN] onAdLoaded - skip disabled, navigating immediately')
            await this.navigateToNext()
          }
          return
        }

        await logPreLoginEvent(
          '[PRE-LOGIN] onAdLoaded - Samba ad loaded, waiting for ad-started event',
          {
            isSambaAd,
          },
        )

        await logPreLoginEvent('[PRE-LOGIN] onAdLoaded - END', {
          adLoaded: this.adLoaded,
          adStarted: this.adStarted,
          isBrokenAd: adData?.isBrokenAd,
          isSambaAd,
        })
      },

      async onAdStarted(adData) {
        await logPreLoginEvent('[PRE-LOGIN] onAdStarted - START', {
          previousAdStarted: this.adStarted,
          adLoaded: this.adLoaded,
          adIntegrationId: this.adIntegrationId,
          skipEnabled: this.skipEnabled,
          timerStarted: this.timerStarted,
          adData,
          timestamp: new Date().toISOString(),
        })

        this.adStarted = true

        this.isSambaAd = adData?.isSambaAd || false

        await logPreLoginEvent('[PRE-LOGIN] onAdStarted - set adStarted=true', {
          adStarted: this.adStarted,
          isSambaAd: this.isSambaAd,
        })

        logUserAction({
          type: 'ad_event',
          label: 'Pre-Login Ad Started',
          data: {
            integrationId: this.adIntegrationId,
          },
        })

        await logPreLoginEvent('[PRE-LOGIN] onAdStarted - sent event', {
          type: 'ad_event',
          label: 'Pre-Login Ad Started',
          data: {
            integrationId: this.adIntegrationId,
          },
        })

        if (this.skipEnabled && !this.timerStarted && !this.isSambaAd) {
          await logPreLoginEvent('[PRE-LOGIN] onAdStarted - starting skip timer (non-Samba)', {
            skipEnabled: this.skipEnabled,
            timerStarted: this.timerStarted,
            skipDelay: this.skipDelay,
            isSambaAd: this.isSambaAd,
          })
          this.startTimer()
        } else {
          await logPreLoginEvent('[PRE-LOGIN] onAdStarted - NOT starting timer', {
            skipEnabled: this.skipEnabled,
            timerStarted: this.timerStarted,
            isSambaAd: this.isSambaAd,
            reason: this.isSambaAd
              ? 'Samba ad - using player skip button'
              : !this.skipEnabled
              ? 'skipEnabled is false'
              : 'timer already started',
          })
        }

        await logPreLoginEvent('[PRE-LOGIN] onAdStarted - END', {
          adStarted: this.adStarted,
          timerStarted: this.timerStarted,
          isSambaAd: this.isSambaAd,
        })
      },

      startTimer() {
        logPreLoginEvent('[PRE-LOGIN] startTimer - START', {
          previousTimerStarted: this.timerStarted,
          skipDelay: this.skipDelay,
          errorTimerDuration: this.errorTimerDuration,
          previousTimer: this.timer,
          previousShowNextButton: this.showNextButton,
        })

        this.timerStarted = true

        const timerDuration =
          this.errorTimerDuration !== null ? this.errorTimerDuration : this.skipDelay

        if (timerDuration > 0) {
          this.timer = timerDuration
          logPreLoginEvent('[PRE-LOGIN] startTimer - setting up interval', {
            timer: this.timer,
            timerDuration,
            skipDelay: this.skipDelay,
            errorTimerDuration: this.errorTimerDuration,
          })

          this.intervalId = setInterval(() => {
            this.timer = this.timer - 1
            logPreLoginEvent('[PRE-LOGIN] startTimer - tick', {
              timer: this.timer,
              showNextButton: this.showNextButton,
            })

            if (this.timer === 0) {
              clearInterval(this.intervalId)
              this.showNextButton = true
              logPreLoginEvent('[PRE-LOGIN] startTimer - timer reached 0, showing button', {
                timer: this.timer,
                showNextButton: this.showNextButton,
              })

              if (this.errorTimerDuration !== null) {
                this.errorTimerDuration = null
                logPreLoginEvent('[PRE-LOGIN] startTimer - error timer completed, navigating', {
                  adStarted: this.adStarted,
                  adLoaded: this.adLoaded,
                  nextRoute: this.nextRoute,
                })
                setTimeout(() => {
                  this.navigateToNext()
                }, 100)
              }
            }
          }, 1000)
        } else {
          this.timer = null
          this.showNextButton = true
          logPreLoginEvent('[PRE-LOGIN] startTimer - skipDelay is 0, showing button immediately', {
            timer: this.timer,
            showNextButton: this.showNextButton,
            skipDelay: this.skipDelay,
          })
        }

        logPreLoginEvent('[PRE-LOGIN] startTimer - END', {
          timerStarted: this.timerStarted,
          timer: this.timer,
          showNextButton: this.showNextButton,
          hasIntervalId: !!this.intervalId,
        })
      },

      async onAdCompleted() {
        await logPreLoginEvent('[PRE-LOGIN] onAdCompleted - START', {
          adLoaded: this.adLoaded,
          adStarted: this.adStarted,
          adIntegrationId: this.adIntegrationId,
          timer: this.timer,
          timerStarted: this.timerStarted,
          showNextButton: this.showNextButton,
          timestamp: new Date().toISOString(),
        })

        logUserAction({
          type: 'ad_event',
          label: 'Pre-Login Ad Completed',
          data: {
            integrationId: this.adIntegrationId,
            watchedFull: true,
          },
        })

        await logPreLoginEvent('[PRE-LOGIN] onAdCompleted - logged user action', {
          integrationId: this.adIntegrationId,
        })

        // Auto-navigate when video completes (regardless of skipEnabled)
        await logPreLoginEvent('[PRE-LOGIN] onAdCompleted - setting timeout to navigate', {
          timeout: 1000,
          nextRoute: this.nextRoute,
        })

        setTimeout(async () => {
          await logPreLoginEvent('[PRE-LOGIN] onAdCompleted - timeout fired, navigating', {
            nextRoute: this.nextRoute,
          })
          await this.navigateToNext()
        }, 1000)

        await logPreLoginEvent('[PRE-LOGIN] onAdCompleted - END', {})
      },

      onAdError(error) {
        logPreLoginEvent('[PRE-LOGIN] onAdError - START', {
          error,
          errorMessage: error?.message,
          errorString: String(error),
          errorType: typeof error,
          errorStack: error?.stack,
          adLoaded: this.adLoaded,
          adStarted: this.adStarted,
          adIntegrationId: this.adIntegrationId,
          timestamp: new Date().toISOString(),
        })

        logUserAction({
          type: 'ad_event',
          label: 'Pre-Login Ad Error',
          data: {
            integrationId: this.adIntegrationId,
            error: error?.message || 'Unknown error',
          },
        })

        logPreLoginEvent('[PRE-LOGIN] onAdError - logged user action, navigating', {
          integrationId: this.adIntegrationId,
          error: error?.message || 'Unknown error',
          nextRoute: this.nextRoute,
        })

        if (!this.timerStarted) {
          this.navigateToNext()
        }

        logPreLoginEvent('[PRE-LOGIN] onAdError - END', {})
      },

      onAdSkipped() {
        logPreLoginEvent('[PRE-LOGIN] onAdSkipped - START', {
          adIntegrationId: this.adIntegrationId,
          isSambaAd: this.isSambaAd,
          timestamp: new Date().toISOString(),
        })

        logUserAction({
          type: 'ad_event',
          label: 'Pre-Login Ad Skipped',
          data: {
            integrationId: this.adIntegrationId,
            skipped: true,
          },
        })

        logPreLoginEvent('[PRE-LOGIN] onAdSkipped - navigating', {
          nextRoute: this.nextRoute,
        })

        setTimeout(() => {
          this.navigateToNext()
        }, 100)
      },

      onAdErrorTimerStart(data) {
        logPreLoginEvent('[PRE-LOGIN] onAdErrorTimerStart - START', {
          data,
          timerStarted: this.timerStarted,
          timer: this.timer,
          duration: data?.duration || 3,
        })

        if (!this.timerStarted) {
          this.errorTimerDuration = data?.duration || 3

          this.startTimer()

          logPreLoginEvent('[PRE-LOGIN] onAdErrorTimerStart - timer started', {
            timer: this.timer,
            timerStarted: this.timerStarted,
            errorTimerDuration: this.errorTimerDuration,
          })
        } else {
          logPreLoginEvent('[PRE-LOGIN] onAdErrorTimerStart - timer already started, skipping', {
            timer: this.timer,
            timerStarted: this.timerStarted,
          })
        }

        logPreLoginEvent('[PRE-LOGIN] onAdErrorTimerStart - END', {})
      },

      async navigateToNext() {
        await logPreLoginEvent('[PRE-LOGIN] navigateToNext - START', {
          nextRoute: this.nextRoute,
          isPreview: this.baseData?.development?.preview || false,
          baseDataDevelopment: this.baseData?.development,
          hasRouter: !!this.$router,
          currentRoute: this.$route?.path,
          adLoaded: this.adLoaded,
          adStarted: this.adStarted,
          timer: this.timer,
          showNextButton: this.showNextButton,
          timestamp: new Date().toISOString(),
        })

        if (this.baseData?.development?.preview) {
          await logPreLoginEvent('[PRE-LOGIN] navigateToNext - is preview, NOT navigating', {
            isPreview: true,
            baseDataDevelopment: this.baseData?.development,
            nextRoute: this.nextRoute,
          })
          return
        }

        await logPreLoginEvent('[PRE-LOGIN] navigateToNext - NOT preview, attempting navigation', {
          nextRoute: this.nextRoute,
          hasRouter: !!this.$router,
          routerType: typeof this.$router,
          currentRoute: this.$route?.path,
        })

        if (!this.$router) {
          await logPreLoginEvent('[PRE-LOGIN] navigateToNext - ERROR: no router available', {
            hasRouter: false,
            nextRoute: this.nextRoute,
          })
          return
        }

        try {
          await logPreLoginEvent('[PRE-LOGIN] navigateToNext - calling screens.advance()', {
            routerExists: !!this.$router,
          })

          await screens.advance()

          await logPreLoginEvent('[PRE-LOGIN] navigateToNext - screens.advance() succeeded', {})
        } catch (error) {
          await logPreLoginEvent(
            '[PRE-LOGIN] navigateToNext - screens.advance() ERROR',
            {
              error: error?.message || String(error),
              errorStack: error?.stack,
              errorType: typeof error,
            },
            'warn',
          )
          logPreLoginEvent(
            '[PRE-LOGIN] navigateToNext - router.push console error',
            {
              error: error?.message,
              errorStack: error?.stack,
            },
            'warn',
          )
        }

        await logPreLoginEvent('[PRE-LOGIN] navigateToNext - END', {
          nextRoute: this.nextRoute,
        })
      },
    },
  }
</script>

<style scoped lang="scss">
  .pre-login-root {
    width: 100%;
    height: 100%;
    position: relative;
  }

  .contentWrapper {
    position: relative;
    z-index: 10;
    width: 100vw;
    max-width: 100vw;
    height: 100vh;
    max-height: 100vh;
    padding: 20px 0 0 0;
    background: transparent !important;
    overflow: hidden;
    box-sizing: border-box;
  }

  .postLogin-container {
    max-width: 600px;
    text-align: center;
    margin-left: auto;
    margin-right: auto;
    position: relative;
    z-index: 1;
    background: transparent !important;
  }

  .postLogin-container.preview-mode {
    max-width: 100%;
    width: 100%;
  }

  .postLogin-container > p {
    color: inherit;
    font-size: 16px;
    margin: 10px 0;
    position: relative;
    z-index: 100;
  }

  .ads-area {
    width: 100%;
    max-width: min(600px, 100vw);
    margin: 0 auto 30px auto;
    overflow: hidden;
    box-sizing: border-box;
  }

  .ads-area iframe {
    max-width: 100%;
    height: auto;
  }

  .ads-area .embedly-responsive {
    max-width: 100%;
    overflow: hidden;
  }

  .redirect-area {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 20px;
    position: relative;
    z-index: 100;
  }

  .redirect-time {
    font-size: 12px;
    background: #f4f4f4;
    display: inline-block;
    padding: 10px 20px;
    border-radius: 32px;
    font-weight: 600;
    color: #808080;
    margin-right: 10px;
  }

  .next-btn {
    background: #55acee;
    padding: 10px 20px;
    border-radius: 32px;
    color: white;
    text-decoration: none;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    border: none;
    outline: none;
    transition: all 0.3s ease;
    position: relative;
    z-index: 100;
  }

  .next-btn:hover {
    background: #3d8ed4;
    transform: scale(1.05);
  }

  .next-btn:active {
    transform: scale(0.95);
  }

  ::v-deep .thankyou-layout-bg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 0 !important;
    pointer-events: none;
  }

  ::v-deep .thankyou-layout-bg > * {
    pointer-events: none !important;
    z-index: 0 !important;
  }
</style>

<style>
  .debug-banner {
    position: fixed !important;
    top: 20px !important;
    left: 20px !important;
    z-index: 9999 !important;
    padding: 6px 12px !important;
    background: rgba(0, 0, 0, 0.6) !important;
    color: #fff !important;
    font-size: 14px !important;
    border-radius: 4px !important;
    pointer-events: none !important;
  }

  html,
  body {
    overflow-y: auto !important;
    overflow-x: hidden !important;
    width: 100vw !important;
    max-width: 100vw !important;
    min-height: 100vh !important;
    height: auto !important;
  }

  ::-webkit-scrollbar {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
  }

  *::-webkit-scrollbar {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
  }

  * {
    -ms-overflow-style: none !important;
    scrollbar-width: none !important;
  }
</style>
